import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { tierIndexFor, quantityForTier } from '@theme/eme-quantity-breaks-core';

class EmeQuantityBreaks extends Component {
  #form = null;

  connectedCallback() {
    super.connectedCallback();
    this.#form = this.closest('product-form-component');
    this.#form?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#sync);
    this.#form?.addEventListener('input', this.#handleInput);
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#form?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#sync);
    this.#form?.removeEventListener('input', this.#handleInput);
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#form = null;
  }

  /** @param {Event} event */
  selectTier(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-quantity]') : null;
    const selector = this.#selector;
    if (!(button instanceof HTMLButtonElement) || button.disabled || !selector?.getCurrentValues) return;

    const { min, step } = selector.getCurrentValues();
    const quantity = quantityForTier(Number(button.dataset.quantity), { min, step, max: selector.getEffectiveMax() });
    selector.setValue(String(quantity));
    selector.onQuantityChange();
    selector.updateButtonStates();
    this.#sync();
  }

  get #selector() {
    return /** @type {any} */ (this.#form?.querySelector('quantity-selector-component') ?? null);
  }

  get #tiers() {
    const { tiers } = this.refs;
    if (Array.isArray(tiers)) return tiers;
    return tiers ? [tiers] : [];
  }

  #handleInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') this.#sync();
  };

  #handleProductSelect = async (event) => {
    try {
      await event.promise;
    } catch (error) {
      if (error?.name !== 'AbortError') throw error;
      return;
    }
    requestAnimationFrame(this.#sync);
  };

  #sync = () => {
    const tiers = this.#tiers;
    if (tiers.length === 0) return;
    const quantity = Number.parseInt(this.#selector?.getValue?.() ?? '', 10) || 0;
    const selected = tierIndexFor(quantity, tiers.map((tier) => Number(tier.dataset.quantity)));
    const unavailable = Boolean(this.#form?.querySelector('input[name="quantity"]')?.disabled);
    for (const [index, tier] of tiers.entries()) {
      tier.setAttribute('aria-pressed', String(index === selected));
      tier.disabled = unavailable;
    }
    this.closest('.eme-quantity-breaks')?.toggleAttribute('data-stepper-selected', selected === -1);
  };
}

if (!customElements.get('eme-quantity-breaks')) {
  customElements.define('eme-quantity-breaks', EmeQuantityBreaks);
}
